/**
 * Pre-match pick flow (06 §4) — pure functions over `s.match.lobby`.
 * The server wires socket messages to `lobbyPick`/`lobbyLock`; `round.ts`
 * calls `lobbyFinalize`/`lobbyReveal` at phase boundaries. Bots pick via
 * `botLobbyChoice` (07 §8).
 */
import { DEFAULT_DECK, POWERUP_IDS } from './powerups.js'
import type { SimState } from './types.js'

export const DECK_SIZE = 3

const isPoolId = (id: string): boolean => POWERUP_IDS.includes(id)

/** Replace a player's deck (≤ 3 unique pool ids). Only during LOBBY_PICK. */
export function lobbyPick(s: SimState, playerId: string, ids: readonly string[]): boolean {
  if (s.match.phase !== 'LOBBY_PICK') return false
  if (!s.players.some((p) => p.id === playerId)) return false
  const deck: string[] = []
  for (const id of ids) {
    if (!isPoolId(id) || deck.includes(id)) continue
    deck.push(id)
    if (deck.length === DECK_SIZE) break
  }
  s.match.lobby.decks[playerId] = deck
  return true
}

/** Select (or change) the lock. Only during LOBBY_LOCK; must be in the deck. */
export function lobbyLock(s: SimState, playerId: string, id: string): boolean {
  if (s.match.phase !== 'LOBBY_LOCK') return false
  const deck = s.match.lobby.decks[playerId]
  if (!deck || !deck.includes(id)) return false
  s.match.lobby.locks[playerId] = id
  return true
}

/** 06 §4 defaults: decks < 3 filled from DEFAULT_DECK in order (no dupes);
 *  missing locks → deck slot 0. Idempotent; safe to call at both phase ends. */
export function lobbyFinalize(s: SimState): void {
  const lobby = s.match.lobby
  for (const p of s.players) {
    const deck = (lobby.decks[p.id] ?? []).filter(isPoolId).slice(0, DECK_SIZE)
    for (const d of DEFAULT_DECK) {
      if (deck.length >= DECK_SIZE) break
      if (!deck.includes(d)) deck.push(d)
    }
    lobby.decks[p.id] = deck
    const lock = lobby.locks[p.id]
    if (s.match.phase !== 'LOBBY_PICK' && (!lock || !deck.includes(lock))) lobby.locks[p.id] = deck[0] ?? null
  }
}

/** REVEAL: the lock becomes the player's power-up list for the match. */
export function lobbyReveal(s: SimState): void {
  lobbyFinalize(s)
  for (const p of s.players) {
    const lock = s.match.lobby.locks[p.id]
    p.powerUps = lock ? [lock, ...p.imprints] : [...p.imprints]
  }
}

export type BotTier = 'ROOKIE' | 'PRO' | 'ACE' | 'TUTOR'

/** 07 §8 deck weights (PRO/ACE); ROOKIE/TUTOR uniform. */
const DECK_WEIGHTS: Record<string, number> = {
  heavy_disc: 1.2, banker: 1.3, riposte: 1.2, bulwark: 0.8, twin_disc: 1.0, ghost_step: 0.9, long_arm: 0.8, sprinter: 0.9,
}
/** Lock preference — "the card the tier plays best". */
const LOCK_PREF: Record<BotTier, string[]> = {
  ROOKIE: ['bulwark', 'heavy_disc', 'sprinter', 'twin_disc', 'long_arm', 'ghost_step', 'banker', 'riposte'],
  TUTOR: ['bulwark', 'heavy_disc', 'sprinter', 'twin_disc', 'long_arm', 'ghost_step', 'banker', 'riposte'],
  PRO: ['heavy_disc', 'banker', 'riposte', 'twin_disc', 'sprinter', 'ghost_step', 'bulwark', 'long_arm'],
  ACE: ['riposte', 'banker', 'heavy_disc', 'twin_disc', 'ghost_step', 'sprinter', 'long_arm', 'bulwark'],
}

/** Seeded bot pick: 3 weighted draws without replacement, lock by tier preference. */
export function botLobbyChoice(rng: () => number, tier: BotTier): { deck: string[]; lock: string } {
  const weighted = tier === 'PRO' || tier === 'ACE'
  const pool = [...POWERUP_IDS]
  const deck: string[] = []
  while (deck.length < DECK_SIZE && pool.length) {
    const weights = pool.map((id) => (weighted ? DECK_WEIGHTS[id] ?? 1 : 1))
    const total = weights.reduce((a, b) => a + b, 0)
    let r = rng() * total
    let idx = 0
    for (; idx < pool.length - 1; idx++) { r -= weights[idx]; if (r < 0) break }
    deck.push(pool.splice(idx, 1)[0])
  }
  const lock = LOCK_PREF[tier].find((id) => deck.includes(id)) ?? deck[0]
  return { deck, lock }
}
